import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AppDataGrid, type AppColumn } from "../../../components/AppDataGrid";
import { ExportMenu } from "../../accounting/ExportMenu";
import { useNotify } from "../../../components/notifications";
import { useSaveFlow, useConfirm } from "../../../components/confirm";
import {
  useCityAreas,
  useAllAreas,
  useCreateArea,
  useUpdateArea,
  useLinkArea,
  useUnlinkArea,
  useCreateStandaloneArea,
  useUpdateStandaloneArea,
  errMessage,
  type AreaRow,
  type StagedArea,
} from "./city";
import { useContentRectStyle } from "./useContentRectStyle";
import { useHotkeys, formatCombo } from "../../../components/hotkeys";

interface Props {
  /** The saved City Oid (server mode). Absent ⇒ a new, unsaved City (staged mode). */
  cityId?: string;
  /** Staged areas for a new City (in-memory until the parent is saved). */
  staged?: StagedArea[];
  onStaged?: (next: StagedArea[]) => void;
  /** Read-only (the City detail is in VIEW mode): hide the New/Link/Unlink/Edit
   *  controls and the row-action columns; the grid just lists the areas. */
  readOnly?: boolean;
}

const newKey = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `k${Date.now()}-${Math.round(Math.random() * 1e6)}`;

/**
 * The Area child grid inside the City detail/edit form — mirrors the legacy
 * `City_DetailView ▸ Area` nested list (many-to-many via dbo.City_Areas).
 *
 * Two modes:
 *  • server mode (saved city) — New/Link/Unlink/Edit commit immediately;
 *  • staged mode (new, unsaved city) — operations stage in memory and commit
 *    when the parent City is saved, so areas can be added before the first save
 *    (XAF parity), and Cancel discards them.
 */
export function CityAreasPanel({ cityId, staged = [], onStaged, readOnly = false }: Props) {
  const staging = !cityId;
  // Embedded with neither a saved city nor a staging handler ⇒ gate until saved.
  const gated = staging && !onStaged;
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { confirm } = useConfirm();
  const { data: serverAreas = [], isFetching } = useCityAreas(cityId);
  const [linkOpen, setLinkOpen] = useState(false);
  // GoLive's "Link" shows the FULL Area master list (not just areas unlinked from
  // THIS city), so a just-created area appears there immediately and any area can
  // be linked; re-linking one already on this city is a harmless no-op (the junction
  // insert is IF-NOT-EXISTS). AI previously used the city-specific "unlinked" set,
  // which hid a freshly-added area from its own Link picker.
  const { data: allAreas = [] } = useAllAreas(linkOpen);
  // The master 'sm-all-areas' query is gated on linkOpen, so while the picker is
  // closed a rename only marks it stale — the re-open doesn't always refetch. Force
  // a fresh fetch every time the picker opens so it never shows an old area name.
  const qc = useQueryClient();
  useEffect(() => {
    if (linkOpen) void qc.refetchQueries({ queryKey: ["sm-all-areas"] });
  }, [linkOpen, qc]);
  const createArea = useCreateArea(cityId);
  const updateArea = useUpdateArea(cityId);
  const linkArea = useLinkArea(cityId);
  const unlinkArea = useUnlinkArea(cityId);
  // Standalone Area create/rename for staged (new, unsaved) city — persists to
  // dbo.Area immediately (GoLive parity), the City↔Area link is staged.
  const createStandaloneArea = useCreateStandaloneArea();
  const updateStandaloneArea = useUpdateStandaloneArea();

  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  // Area popup: undefined = closed, null = New, row = Edit.
  const [areaDlg, setAreaDlg] = useState<AreaRow | null | undefined>(undefined);
  const [areaName, setAreaName] = useState("");
  const [areaErr, setAreaErr] = useState("");
  const [linkSel, setLinkSel] = useState<Set<string | number>>(new Set());
  const [linkSearch, setLinkSearch] = useState(""); // Link-popup search box (LIMS auto-filter)
  const [busy, setBusy] = useState(false);
  const [popupMax, setPopupMax] = useState(false); // nested popup opens at normal centred size; toggle to maximize
  const popupMaxStyle = useContentRectStyle(popupMax && (areaDlg !== undefined || linkOpen));
  // True while the Area edit popup was opened from the Link picker (so we reopen
  // the picker, refreshed, when the edit closes — GoLive keeps the lookup up).
  const [editFromLink, setEditFromLink] = useState(false);

  // Rows shown: staged list (mapped) when new, else server areas.
  const rows: AreaRow[] = staging ? staged.map((s) => ({ id: s.key, areaName: s.name })) : serverAreas;

  /** Area Oids ALREADY on this city. GoLive disables the Link picker's edit pencil
   *  for these (an area already in the collection is not editable from the lookup),
   *  and leaves it enabled for the rest. */
  const linkedAreaIds = new Set(
    (staging ? (staged.map((s) => s.areaId).filter(Boolean) as string[]) : serverAreas.map((a) => a.id)).map((id) =>
      id.toLowerCase(),
    ),
  );
  const isLinked = (id: string) => linkedAreaIds.has(id.toLowerCase());

  const openNew = () => {
    setLinkOpen(false);
    setPopupMax(false);
    setAreaDlg(null);
    setAreaName("");
    setAreaErr("");
  };
  const openEditRow = (r: AreaRow) => {
    setLinkOpen(false);
    setPopupMax(false);
    setAreaDlg(r);
    setAreaName(r.areaName ?? "");
    setAreaErr("");
  };
  const closeAreaDlg = () => {
    setAreaDlg(undefined);
    // Reopen the (refreshed) Link picker if the edit was launched from it.
    if (editFromLink) {
      setEditFromLink(false);
      setLinkOpen(true);
    }
  };

  const submitArea = async () => {
    if (!areaName.trim()) {
      setAreaErr("'Area' must not to be empty.");
      return;
    }
    setAreaErr("");
    const name = areaName.trim();
    if (staging) {
      // GoLive persists a new Area to dbo.Area immediately (the City↔Area link is
      // staged and committed on city save), so it's a real master row visible
      // everywhere — including other apps on the shared DB. Rename persists too.
      try {
        if (areaDlg) {
          // areaDlg.id is a STAGED KEY when the edit came from the city's own Area
          // grid, but the area's DB Oid when it came from the Link picker (rows =
          // allAreas). Resolve the real Area id from either.
          const stagedItem = staged.find((s) => s.key === areaDlg.id);
          const areaId = stagedItem?.areaId ?? areaDlg.id;
          await updateStandaloneArea.mutateAsync({ areaId, areaName: name });
          // Reflect the rename on any staged link that points to this area.
          onStaged?.(staged.map((s) => (s.areaId === areaId || s.key === areaDlg.id ? { ...s, name } : s)));
        } else {
          const areaId = await createStandaloneArea.mutateAsync(name);
          onStaged?.([...staged, { key: newKey(), name, areaId }]);
        }
        closeAreaDlg();
      } catch (e) {
        setAreaErr(errMessage(e));
      }
      return;
    }
    await runSave({
      save: async () => {
        if (areaDlg) await updateArea.mutateAsync({ areaId: areaDlg.id, areaName: name });
        else await createArea.mutateAsync(name);
      },
      successMessage: "Saved successfully.",
      onSaved: () => closeAreaDlg(),
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  const openLink = () => {
    setAreaDlg(undefined);
    setPopupMax(false);
    setLinkSel(new Set());
    setLinkSearch("");
    setLinkOpen(true);
  };
  // Link picker source: the full Area master list (GoLive parity). Every area —
  // including one just created on a brand-new city (persisted immediately) — is in
  // dbo.Area, so both modes list allAreas; a saved city shows every persisted area.
  const stagedAreaIds = new Set(staged.map((s) => s.areaId).filter(Boolean) as string[]);
  const linkSource: AreaRow[] = allAreas;
  const linkRows = linkSource.filter((a) => {
    const q = linkSearch.trim().toLowerCase();
    return !q || (a.areaName ?? "").toLowerCase().includes(q);
  });
  const doLink = async () => {
    if (linkSel.size === 0) {
      notify.warning("Select at least one area to link.");
      return;
    }
    if (staging) {
      // Skip areas already staged onto this city — re-selecting one is a no-op.
      const add = linkSource
        .filter((a) => linkSel.has(a.id))
        .filter((a) => !stagedAreaIds.has(a.id))
        .map((a) => ({ key: newKey(), name: a.areaName ?? "", areaId: a.id }));
      onStaged?.([...staged, ...add]);
      setLinkOpen(false);
      setLinkSel(new Set());
      return;
    }
    setBusy(true);
    try {
      for (const id of linkSel) await linkArea.mutateAsync(String(id));
      setLinkOpen(false);
      setLinkSel(new Set());
    } catch (e) {
      notify.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doUnlink = async () => {
    if (selectedIds.size === 0) {
      notify.warning("Select at least one area to unlink.");
      return;
    }
    if (staging) {
      onStaged?.(staged.filter((s) => !selectedIds.has(s.key)));
      setSelectedIds(new Set());
      return;
    }
    const ok = await confirm({
      title: "Unlink",
      tone: "danger",
      confirmLabel: "Unlink",
      message: `Remove ${selectedIds.size} area${selectedIds.size === 1 ? "" : "s"} from this city? (The area itself is kept.)`,
    });
    if (!ok) return;
    try {
      for (const id of selectedIds) await unlinkArea.mutateAsync(String(id));
      setSelectedIds(new Set());
    } catch (e) {
      notify.error(errMessage(e));
    }
  };

  const allAreaColumns: AppColumn<AreaRow>[] = [
    {
      // Row icon: opens the Area edit popup — the same one the toolbar Edit opens (owner request 2026-10-14;
      // the in-place rename it used to start is removed).
      field: "detailedit",
      header: "",
      sortable: false,
      filterable: false,
      noExport: true,
      width: 44,
      align: "center",
      render: (r) => (
        <button
          className="pts-icon-btn edit"
          title="Edit"
          disabled={gated}
          onClick={(e) => {
            e.stopPropagation();
            openEditRow(r);
          }}
        >
          <i className="ti ti-edit" />
        </button>
      ),
    },
    {
      field: "areaName",
      header: "Area",
      minWidth: 260,
      // G5's grid summary under the Area column (owner request 2026-10-14).
      summary: (list) => `Count=${list.length}`,
      text: (r) => r.areaName ?? "",
      compare: (a, b) => (a.areaName ?? "").localeCompare(b.areaName ?? "", undefined, { sensitivity: "base" }),
      render: (r) => <span>{r.areaName ?? ""}</span>,
    },
  ];
  // View mode (read-only): drop the row-action (edit) column — just list the areas.
  const columns: AppColumn<AreaRow>[] = readOnly
    ? allAreaColumns.filter((c) => c.field === "areaName")
    : allAreaColumns;

  const linkColumns: AppColumn<AreaRow>[] = [
    {
      field: "edit",
      header: "",
      sortable: false,
      filterable: false,
      width: 44,
      align: "center",
      // GoLive DISABLES the edit pencil for an area already in this city's Area
      // collection (it is edited from the grid, not from the lookup); areas not yet
      // linked keep an enabled pencil.
      render: (r) => {
        const linked = isLinked(r.id);
        return (
          <button
            className="pts-icon-btn edit"
            disabled={linked}
            title={linked ? "Already added to this city — edit it from the Area grid" : "Edit"}
            style={linked ? { opacity: 0.35, cursor: "not-allowed" } : undefined}
            onClick={(e) => {
              e.stopPropagation();
              if (linked) return;
              setEditFromLink(true);
              openEditRow(r);
            }}
          >
            <i className="ti ti-edit" />
          </button>
        );
      },
    },
    {
      field: "areaName",
      header: "Area",
      minWidth: 320,
      text: (r) => r.areaName ?? "",
      compare: (a, b) => (a.areaName ?? "").localeCompare(b.areaName ?? "", undefined, { sensitivity: "base" }),
    },
  ];

  // Toolbar Edit: the Area popup for the single ticked row.
  const canEditSelected = !gated && selectedIds.size === 1;
  const editSelected = () => {
    const row = rows.find((r) => selectedIds.has(r.id));
    if (canEditSelected && row) openEditRow(row);
  };

  useHotkeys([
    { combo: "alt+n", label: "New", group: "Area", order: 1, disabled: gated, run: openNew },
    { combo: "alt+l", label: "Link", group: "Area", order: 2, disabled: gated, run: openLink },
    {
      combo: "alt+k",
      label: "Unlink",
      group: "Area",
      order: 3,
      disabled: gated || selectedIds.size === 0,
      run: () => {
        void doUnlink();
      },
    },
    // Toolbar Edit is a permanently-disabled placeholder (GoLive edits via row pencils).
    { combo: "alt+e", label: "Edit", group: "Area", order: 4, disabled: !canEditSelected, run: editSelected },
  ]);

  return (
    <div className="acc-subgrid">
      <div className="acc-sub-title">Area</div>
      <div className="acc-sub-toolbar">
        {/* Action buttons hidden in view mode (read-only) — only Export remains. */}
        {!readOnly && (
          <>
            <button className="ca-btn" title={`New (${formatCombo("alt+n")})`} onClick={openNew} disabled={gated}>
              <i className="ti ti-plus" /> New
            </button>
            <button className="ca-btn" title={`Link (${formatCombo("alt+l")})`} onClick={openLink} disabled={gated}>
              <i className="ti ti-link" /> Link
            </button>
            <button
              className="ca-btn"
              title={`Unlink (${formatCombo("alt+k")})`}
              onClick={() => void doUnlink()}
              disabled={gated || selectedIds.size === 0}
            >
              <i className="ti ti-unlink" /> Unlink
            </button>
            {/* Edit opens the Area popup for the ONE ticked row (owner request 2026-10-09). GoLive
                keeps this button disabled and uses a second row icon for it; that icon was removed
                here, so the toolbar Edit is now the way to the popup. */}
            <button className="ca-btn" title={`Edit (${formatCombo("alt+e")})`} onClick={editSelected} disabled={!canEditSelected}>
              <i className="ti ti-edit" /> Edit
            </button>
          </>
        )}
        <ExportMenu
          triggerClassName="ca-btn"
          filename="Area"
          headers={["Area"]}
          rows={rows.map((a) => [a.areaName ?? ""])}
        />
      </div>
      {gated && <div className="acc-sub-hint">Save the city first, then add or link its areas.</div>}

      <AppDataGrid<AreaRow>
        rows={rows}
        columns={columns}
        getRowId={(r) => r.id}
        // The tick column shows in view mode too, as in G5 (owner request 2026-10-14).
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        selectedRowId={areaDlg?.id ?? null}
        initialSort={{ key: "areaName", dir: "asc" }}
        loading={!staging && isFetching}
        emptyText="No data to display"
        defaultPageSize={20}
      />

      {/* "Area" popup — Area:* field + OK / Cancel. */}
      {areaDlg !== undefined && (
        <div
          className="acc-overlay"
          onMouseDown={(e) => {
            e.stopPropagation();
            closeAreaDlg();
          }}
        >
          <div
            className={`acc-dialog acc-dialog--accent${popupMax ? " acc-dialog-max" : ""}`}
            style={popupMaxStyle}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="acc-dialog-hdr">
              <span>{areaDlg ? `${areaDlg.areaName ?? ""} - Area` : "Area"}</span>
              <span className="acc-dialog-acts">
                <i
                  className={`ti ${popupMax ? "ti-minimize" : "ti-maximize"} acc-dialog-x`}
                  title={popupMax ? "Restore" : "Maximize"}
                  onClick={() => setPopupMax((m) => !m)}
                />
                <i className="ti ti-x acc-dialog-x" onClick={closeAreaDlg} />
              </span>
            </div>
            <div className="acc-dialog-body">
              <div className="acc-field">
                <label className="acc-label">
                  Area<span className="req">*</span>
                </label>
                <input
                  className="acc-input"
                  autoFocus
                  value={areaName}
                  onChange={(e) => {
                    setAreaName(e.target.value);
                    if (areaErr) setAreaErr("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void submitArea();
                  }}
                />
                {areaErr && <div className="acc-err">{areaErr}</div>}
              </div>
            </div>
            <div className="acc-dialog-ftr">
              <button
                className="ca-btn ca-btn-solid"
                onClick={() => void submitArea()}
                disabled={createArea.isPending || updateArea.isPending}
              >
                <span className="tb-i">💾</span> OK
              </button>
              <button className="ca-btn" onClick={closeAreaDlg}>
                <span className="tb-i tb-i-grey">↩︎</span> Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* "Link" picker popup — checkbox grid of existing areas, multi-select. */}
      {linkOpen && (
        <div
          className="acc-overlay"
          onMouseDown={(e) => {
            e.stopPropagation();
            setLinkOpen(false);
          }}
        >
          <div
            className={`acc-dialog acc-dialog-pick acc-dialog--accent${popupMax ? " acc-dialog-max" : ""}`}
            style={popupMaxStyle}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="acc-dialog-hdr">
              <span>Area</span>
              <span className="acc-dialog-acts">
                <i
                  className={`ti ${popupMax ? "ti-minimize" : "ti-maximize"} acc-dialog-x`}
                  title={popupMax ? "Restore" : "Maximize"}
                  onClick={() => setPopupMax((m) => !m)}
                />
                <i className="ti ti-x acc-dialog-x" onClick={() => setLinkOpen(false)} />
              </span>
            </div>
            <div className="acc-dialog-body">
              <div className="acc-pick-search">
                <div className="acc-search">
                  <i className="ti ti-search" />
                  <input
                    placeholder="Search…"
                    autoFocus
                    value={linkSearch}
                    onChange={(e) => setLinkSearch(e.target.value)}
                  />
                  {linkSearch && <i className="ti ti-x acc-search-x" onClick={() => setLinkSearch("")} />}
                </div>
              </div>
              <AppDataGrid<AreaRow>
                rows={linkRows}
                columns={linkColumns}
                getRowId={(r) => r.id}
                selectable
                selectedIds={linkSel}
                onSelectionChange={setLinkSel}
                initialSort={{ key: "areaName", dir: "asc" }}
                emptyText="No areas available to link."
                defaultPageSize={20}
              />
            </div>
            <div className="acc-dialog-ftr">
              <button
                className="ca-btn ca-btn-solid"
                disabled={busy || linkSel.size === 0}
                onClick={() => void doLink()}
              >
                OK
              </button>
              <button className="ca-btn" disabled={busy} onClick={() => setLinkOpen(false)}>
                <span className="tb-i tb-i-grey">↩︎</span> Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
